import { useState } from "react";
import { useSelector } from "react-redux";
import {
  setSelectedAsset,
  setSelectedLastUpdatedDate,
  setSelectedStatus,
  setSelectedColumnFilter,
  clearSelectedColumnFilters,
} from "../../../../../features/dataAccuracySlice";
import { useDispatch } from "react-redux";
import FilterComponent from "../../../../ReusableFIlterComponent/FilterComponent";
import { getSiteInfo } from "../../../../../utils/helpers";
import { selectUserWebsitePermissions } from "../../../../../features/userSlice";

interface DynamicFilterColumn {
  field: string;
  headerName: string;
  isDateRangeFilter?: boolean;
}

interface TaskNameDAFilterProps {
  // Columns from the pinned-left section of the grid (up to where horizontal
  // scrolling starts) that don't already have a dedicated filter below. One
  // filter is generated per entry.
  dynamicColumns?: DynamicFilterColumn[];
  // Flattened rows (same shape as the grid rows) used as the source of
  // options for the dynamic filters.
  rows?: any[];
}

const TaskNameDAFilter = ({
  dynamicColumns = [],
  rows = [],
}: TaskNameDAFilterProps) => {
  const {
    tasksByTaskStructureSharedUid,
    selectedAsset,
    selectedStatus,
    selectedLastUpdatedDate,
    selectedColumnFilters,
  } = useSelector((state: any) => state.dataAccuracy);
  const dispatch = useDispatch();
  const [searchAsset, setSearchAsset] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [dynamicSearchTerms, setDynamicSearchTerms] = useState<
    Record<string, string>
  >({});
  const allWebsites = useSelector(selectUserWebsitePermissions);
  function appendSiteNameToTasks(tasks: any[], websites: any[]): any[] {
    return tasks.map((task) => {
      const siteName = getSiteInfo(websites, task.website_shared_uid);

      return {
        ...task,
        site_name: siteName || null, // add new field
      };
    });
  }

  const updatedTasks = appendSiteNameToTasks(
    tasksByTaskStructureSharedUid || [],
    allWebsites || [] // your website data array
  );

  const filterConfig = [
    {
      key: "Asset",
      label: "Select Asset",
      searchTerm: searchStatus,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchStatus(e.target.value),
      selectedOption: selectedAsset,
      filterName: "site_name",
      data: updatedTasks,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedAsset(updatedSelection)),
      count: selectedAsset && selectedAsset.length,
      colorSelected:
        selectedAsset !== null && selectedAsset.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "Status",
      label: "Select Status",
      searchTerm: searchAsset,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchAsset(e.target.value),
      selectedOption: selectedStatus,
      filterName: "current_status",
      data: updatedTasks,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedStatus(updatedSelection)),
      count: selectedStatus && selectedStatus.length,
      colorSelected:
        selectedStatus !== null && selectedStatus.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "Last Updated",
      label: "Last Updated At",
      isDateRangeFilter: true,
      selectedOption: selectedLastUpdatedDate,
      filterName: "current_status",
      data: updatedTasks,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedLastUpdatedDate(updatedSelection)),
      count: selectedLastUpdatedDate && selectedLastUpdatedDate.length - 1,
      colorSelected:
        selectedLastUpdatedDate !== null && selectedLastUpdatedDate.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: false,
    },
  ];

  // One filter per remaining pinned-left column (up to where the grid starts
  // scrolling horizontally), generated dynamically from whatever columns the
  // table currently renders there. Columns that already have a dedicated
  // filter above (Asset, Status, Last Updated), the scrollable occurrence
  // columns, and the pinned-right columns are excluded upstream, so they're
  // never duplicated here.
  const dynamicFilterConfig = dynamicColumns.map(
    ({ field, headerName, isDateRangeFilter }) => {
      const selectedOption = selectedColumnFilters?.[field] || [];

      if (isDateRangeFilter) {
        return {
          key: headerName,
          label: headerName,
          isDateRangeFilter: true,
          selectedOption,
          filterName: field,
          data: rows,
          onSelectionChange: (updatedSelection: any) =>
            dispatch(
              setSelectedColumnFilter({ field, values: updatedSelection }),
            ),
          count: selectedOption.length > 0 ? selectedOption.length - 1 : 0,
          colorSelected:
            selectedOption.length > 0 ? "#9C27B0" : "#000000DE",
          multiSelect: true,
          showSearch: false,
        };
      }

      return {
        key: headerName,
        label: `Select ${headerName}`,
        searchTerm: dynamicSearchTerms[field] || "",
        onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          setDynamicSearchTerms((prev) => ({
            ...prev,
            [field]: e.target.value,
          })),
        selectedOption,
        filterName: field,
        data: rows,
        onSelectionChange: (updatedSelection: any) =>
          dispatch(
            setSelectedColumnFilter({ field, values: updatedSelection }),
          ),
        count: selectedOption.length,
        colorSelected: selectedOption.length > 0 ? "#9C27B0" : "#000000DE",
        multiSelect: true,
        showSearch: true,
      };
    },
  );

  const clearFilters = () => {
    dispatch(setSelectedAsset([]));
    setSearchAsset("");
    dispatch(setSelectedStatus([]));
    setSearchStatus("");
    dispatch(setSelectedLastUpdatedDate([]));
    dispatch(clearSelectedColumnFilters());
    setDynamicSearchTerms({});
  };
  return (
    <>
      <FilterComponent
        filterConfig={[...filterConfig, ...dynamicFilterConfig]}
        clearFilters={clearFilters}
      />
    </>
  );
};
export default TaskNameDAFilter;
